import { useQuery } from '@tanstack/react-query';
import apiClient from '../utils/apiClient';
import { getTimeRangeMonths } from '../utils/dataHelpers';

// Query keys for individual KPIs
export const MAIN_PERFORMANCE_KPI_KEYS = {
  TOTAL_SALES: 'mainPerformanceTotalSales',
  CUSTOMER_SATISFACTION: 'mainPerformanceCustomerSatisfaction',
  AVERAGE_INVOICE: 'mainPerformanceAverageInvoice',
  NEW_CUSTOMERS: 'mainPerformanceNewCustomers',
  SUBSCRIPTION_RATE: 'mainPerformanceSubscriptionRate',
  SMS_CHARGE_NEGATIVE_DAYS: 'mainPerformanceSmsChargeNegativeDays',
} as const;

// API service functions for individual KPIs
const apiService = {
  fetchTotalSales: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/kpis/total-sales?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch total sales data');
    }
  },

  fetchCustomerSatisfaction: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/kpis/customer-satisfaction?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch customer satisfaction data');
    }
  },

  fetchAverageInvoice: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/kpis/average-invoice?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch average invoice data');
    }
  },

  fetchNewCustomers: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/kpis/new-customers?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch new customers data');
    }
  },

  fetchSubscriptionRate: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/kpis/subscription-rate?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch subscription rate data');
    }
  },

  fetchSmsChargeNegativeDays: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/kpis/sms-charge-negative-days?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch SMS charge negative days data');
    }
  },
};

// Hook for total sales KPI
export function useMainPerformanceTotalSales(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_KPI_KEYS.TOTAL_SALES, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchTotalSales(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Hook for customer satisfaction KPI
export function useMainPerformanceCustomerSatisfaction(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_KPI_KEYS.CUSTOMER_SATISFACTION, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchCustomerSatisfaction(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for average invoice KPI
export function useMainPerformanceAverageInvoice(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_KPI_KEYS.AVERAGE_INVOICE, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchAverageInvoice(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for new customers KPI
export function useMainPerformanceNewCustomers(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_KPI_KEYS.NEW_CUSTOMERS, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchNewCustomers(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for subscription rate KPI
export function useMainPerformanceSubscriptionRate(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_KPI_KEYS.SUBSCRIPTION_RATE, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchSubscriptionRate(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for SMS charge negative days KPI
export function useMainPerformanceSmsChargeNegativeDays(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_KPI_KEYS.SMS_CHARGE_NEGATIVE_DAYS, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchSmsChargeNegativeDays(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}